import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetPath } from '../utils/assetHelper';
import './SettingsAdvancedPage.scss';

interface ApiKey {
  id: string;
  name: string;
  key: string;
  isExpanded: boolean;
  permissions: {
    id: string;
    name: string;
    enabled: boolean;
  }[];
}

interface ConnectedApp {
  id: string;
  name: string;
  enabled: boolean;
}

const SettingsAdvancedPage = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([
    {
      id: '1',
      name: 'API Name Key 1',
      key: '<API key>',
      isExpanded: false,
      permissions: [
        { id: '1', name: 'Resolution 1', enabled: true },
        { id: '2', name: 'Resolution 2', enabled: false },
        { id: '3', name: 'Resolution 3', enabled: true }
      ]
    },
    {
      id: '2',
      name: 'API Name Key 2',
      key: '<API key>',
      isExpanded: false,
      permissions: [
        { id: '1', name: 'Resolution 1', enabled: true },
        { id: '2', name: 'Resolution 2', enabled: false },
        { id: '3', name: 'Resolution 3', enabled: true }
      ]
    }
  ]);

  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([
    { id: '1', name: 'Application name 01', enabled: false },
    { id: '2', name: 'Application name 02', enabled: true }
  ]);

  // Add error state
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Advanced Settings - Phantom Clash';
    
    // Initialize tooltips if Bootstrap is available
    if (typeof window !== 'undefined' && (window as any).bootstrap?.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].map(tooltipTriggerEl => new (window as any).bootstrap.Tooltip(tooltipTriggerEl));
    }
  }, []);

  const toggleApiKeyExpansion = (id: string) => {
    setApiKeys(prevKeys => 
      prevKeys.map(key => 
        key.id === id ? { ...key, isExpanded: !key.isExpanded } : key
      )
    );
  };

  const togglePermission = (keyId: string, permissionId: string) => {
    setApiKeys(prevKeys => 
      prevKeys.map(key => 
        key.id === keyId 
          ? {
              ...key,
              permissions: key.permissions.map(perm => 
                perm.id === permissionId 
                  ? { ...perm, enabled: !perm.enabled }
                  : perm
              )
            }
          : key
      )
    );
  };

  const toggleAppConnection = (appId: string) => {
    setConnectedApps(prevApps => 
      prevApps.map(app => 
        app.id === appId ? { ...app, enabled: !app.enabled } : app
      )
    );
  };

  const generateApiKey = () => {
    // In a real application, this would make an API call to generate a new key
    alert('API key generation would happen here');
  };

  const revokeAccess = (keyId: string) => {
    // In a real application, this would make an API call to revoke the key
    setApiKeys(prevKeys => prevKeys.filter(key => key.id !== keyId));
  };

  return (
    <>
      {/* page menu */}
      <div className="c-page-menu">
        <ul>
          <li><Link to="/settings-profile">Profile</Link></li>
          <li><Link to="/settings-notifications">Notifications</Link></li>
          <li><Link to="/settings-wallet">Wallet</Link></li>
          <li><Link to="/settings-security">Security</Link></li>
          <li><Link to="/settings-privacy">Privacy</Link></li>
          <li><Link to="/settings-display">Theme & Display</Link></li>
          <li className="active"><Link to="/settings-advanced">Advanced Settings</Link></li>
        </ul>
      </div>
      {/* //page menu */}
      
      <div className="d-block w-100">
        {/* Error message if any */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        <div className="box position-relative z-1 overflow-hidden mt-2">
          <div className="c-content-box c-content-box--xspadding overflow-visible c-content-box--setting-width">
            <div className="c-content-box__title">
              <h1 style={{ backgroundImage: 'none' }}><i className="bi bi-lightning-charge-fill"></i> Advanced Settings</h1>
              <div className="c-content-box__title-info"></div>
            </div>
            
            <div className="c-content-box__content" style={{ WebkitMaskImage: 'none' }}>
              <h5 className="c-content-mini-title gray">Developer options for integrating external applications</h5>
              <h3 className="c-content-mini-title-2 mb-4">API Access</h3>
              
              <a href="" 
                className="c-button c-button--bordered c-button--xl c-button--grayicon mt-4 mb-4"
                onClick={(e) => { e.preventDefault(); generateApiKey(); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }} width="16" height="18" viewBox="0 0 16 18" fill="none">
                  <path d="M9.50008 3C9.50008 3.82843 8.82851 4.5 8.00008 4.5C7.17165 4.5 6.50008 3.82843 6.50008 3C6.50008 2.17157 7.17165 1.5 8.00008 1.5C8.82851 1.5 9.50008 2.17157 9.50008 3Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                  <path d="M9.50008 15C9.50008 15.8284 8.82851 16.5 8.00008 16.5C7.17165 16.5 6.50008 15.8284 6.50008 15C6.50008 14.1716 7.17165 13.5 8.00008 13.5C8.82851 13.5 9.50008 14.1716 9.50008 15Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                  <path d="M13.9462 7.29903C13.2288 7.71325 12.3114 7.46744 11.8972 6.75C11.483 6.03256 11.7288 5.11517 12.4462 4.70096C13.1637 4.28674 14.0811 4.53256 14.4953 5.25C14.9095 5.96744 14.6637 6.88482 13.9462 7.29903Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                  <path d="M3.55396 13.299C2.83652 13.7132 1.91914 13.4674 1.50492 12.75C1.09071 12.0326 1.33652 11.1152 2.05396 10.701C2.7714 10.2867 3.68879 10.5326 4.103 11.25C4.51721 11.9674 4.2714 12.8848 3.55396 13.299Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                  <path d="M12.4462 13.299C11.7288 12.8848 11.483 11.9674 11.8972 11.25C12.3114 10.5326 13.2288 10.2867 13.9462 10.701C14.6637 11.1152 14.9095 12.0326 14.4953 12.75C14.0811 13.4674 13.1637 13.7132 12.4462 13.299Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                  <path d="M2.05396 7.29906C1.33652 6.88484 1.09071 5.96746 1.50492 5.25002C1.91914 4.53258 2.83652 4.28677 3.55396 4.70098C4.2714 5.11519 4.51721 6.03258 4.103 6.75002C3.68879 7.46746 2.7714 7.71327 2.05396 7.29906Z" stroke="#4E6D8A" strokeWidth="1.125"/>
                </svg> Generate API Key
              </a>
              
              {/* API Keys */}
              {apiKeys.map(apiKey => (
                <div className="p-api-list mt-2" key={apiKey.id}>
                  <div 
                    className="p-api-list__title" 
                    onClick={() => toggleApiKeyExpansion(apiKey.id)}
                  >
                    <h1><i className="bi bi-code"></i> {apiKey.name}</h1>
                    <span><i className={`bi bi-chevron-${apiKey.isExpanded ? 'up' : 'down'}`}></i></span>
                  </div>
                  
                  {apiKey.isExpanded && (
                    <div className="p-api-list__content">
                      <div className="p-api-list__api">Authorization: Api-Key <small>{apiKey.key}</small></div>
                      <div className="c-content-box__checkboxlist mt-4">
                        {apiKey.permissions.map((permission, index) => (
                          <div 
                            key={permission.id} 
                            className={`c-content-box__checkboxlist-item ${index > 0 ? 'c-divider' : 'pt-0'}`}
                          >
                            <span>{permission.name}</span>
                            <label className="c-switch" id="">
                              <input 
                                type="checkbox" 
                                className="c-switch-input" 
                                checked={permission.enabled}
                                onChange={() => togglePermission(apiKey.id, permission.id)}
                              />
                              <span className="c-switch-label" data-on="On" data-off="Off"></span>
                              <span className="c-switch-handle"></span>
                            </label>
                          </div>
                        ))}
                      </div>
                      <a 
                        href="" 
                        className="c-button c-button--bordered c-button--grayicon mt-2"
                        onClick={(e) => { e.preventDefault(); revokeAccess(apiKey.id); }}
                      >
                        <i className="bi bi-x-circle"></i> Revoke access
                      </a>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Connected Applications */}
              <h3 className="c-content-mini-title-2 mb-4 mt-md-4 mt-3 pb-2 pt-3">Connected Applications</h3>
              <div className="c-content-box__checkboxlist mt-4">
                {connectedApps.map((app, index) => (
                  <div 
                    key={app.id} 
                    className={`c-content-box__checkboxlist-item ${index > 0 ? 'c-divider' : 'pt-0'}`}
                  >
                    <span><i className="bi bi-boxes"></i> {app.name}</span>
                    <label className="c-switch" id="">
                      <input 
                        type="checkbox" 
                        className="c-switch-input" 
                        checked={app.enabled}
                        onChange={() => toggleAppConnection(app.id)}
                      />
                      <span className="c-switch-label" data-on="On" data-off="Off"></span>
                      <span className="c-switch-handle"></span>
                    </label>
                  </div>
                ))}
              </div>
              
              {/* Developer Documentation */}
              <h3 className="c-content-mini-title-2 mb-md-4 mb-1 mt-md-4 mt-3 pb-3">Developer Documentation</h3>
              <a href="" className="c-button c-button--bordered c-button--xl c-button--grayicon mt-2">
                <i className="bi bi-book"></i> Read documentation
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsAdvancedPage; 